import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["filename", "estimate", "estimateStatus", "cutLength", "pierces", "estimatePrice", "file", "material", "thickness", "quantity"]

  static values = {
    requiredMessage: String,
    emailMessage: String,
    numberMessage: String,
    estimateUrl: String,
    rates: Array,
    waitingMessage: String,
    errorMessage: String,
    dxfOnlyMessage: String,
    resultMessage: String,
    promptMessage: String,
    minimumAppliedMessage: String,
    fileTooLargeMessage: String
  }

  connect() {
    this.element.querySelectorAll("[data-validation-messages]").forEach((field) => this.updateMessage(field))
  }

  validate(event) {
    this.updateMessage(event.currentTarget)
  }

  updateMessage(field) {
    field.setCustomValidity("")

    if (field.validity.valueMissing) {
      field.setCustomValidity(field.dataset.validationMessagesRequired || this.requiredMessageValue)
    } else if (field.validity.typeMismatch) {
      field.setCustomValidity(field.dataset.validationMessagesType || this.emailMessageValue)
    } else if (field.validity.badInput || field.validity.rangeUnderflow || field.validity.rangeOverflow || field.validity.stepMismatch) {
      field.setCustomValidity(field.dataset.validationMessagesNumber || this.numberMessageValue)
    }
  }

  showFilename(event) {
    const file = event.currentTarget.files[0]
    this.filenameTarget.textContent = file?.name || this.element.dataset.noFileLabel
    this.requestEstimate()
  }

  async requestEstimate() {
    this.requestController?.abort()
    if (!this.hasFileTarget || !this.fileTarget.files.length) {
      this.estimateTarget.classList.add("hidden")
      return
    }
    if (!this.hasMaterialTarget || !this.hasThicknessTarget || !this.hasQuantityTarget) return

    const file = this.fileTarget.files[0]
    this.estimateTarget.classList.remove("hidden")
    this.estimateStatusTarget.textContent = this.waitingMessageValue
    this.estimatePriceTarget.textContent = ""

    if (!file.name.toLowerCase().endsWith(".dxf")) {
      this.estimateStatusTarget.textContent = this.dxfOnlyMessageValue
      return
    }

    if (file.size > 10 * 1024 * 1024) {
      this.estimateStatusTarget.textContent = this.fileTooLargeMessageValue
      return
    }

    if (!this.materialTarget.value || !this.thicknessTarget.value || !this.quantityTarget.value) {
      this.estimateStatusTarget.textContent = this.promptMessageValue
      return
    }

    this.requestController = new AbortController()
    try {
      const response = await fetch(this.estimateUrlValue, {
        method: "POST",
        body: new FormData(this.element),
        headers: { Accept: "application/json", "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content },
        signal: this.requestController.signal
      })
      const result = await response.json()
      if (!response.ok) throw new Error(result.error || this.errorMessageValue)

      const locale = document.documentElement.lang || "uk"
      const amount = new Intl.NumberFormat(locale, { style: "currency", currency: "UAH", maximumFractionDigits: 2 }).format(result.cutting_cost)
      this.cutLengthTarget.textContent = `${new Intl.NumberFormat(locale, { maximumFractionDigits: 3 }).format(result.cut_length_m)} ${locale === "uk" ? "м" : "m"}`
      this.piercesTarget.textContent = new Intl.NumberFormat(locale).format(result.pierces)
      this.estimatePriceTarget.textContent = amount
      this.estimateStatusTarget.textContent = result.minimum_order_applied ? this.minimumAppliedMessageValue : this.resultMessageValue
    } catch (error) {
      if (error.name === "AbortError") return
      this.estimateStatusTarget.textContent = error.message || this.errorMessageValue
    }
  }
}
